import React, { useEffect, useRef, useState } from 'react';
import Phaser from 'phaser';

const CatchTheBall = ({ incomingData }) => {
    const gameContainerRef = useRef(null);
    const gameInstanceRef = useRef(null);

    // Vistas y datos globales
    const [view, setView] = useState('frontpage');
    const [rankingList, setRankingList] = useState([]);
    const [currentPlayer, setCurrentPlayer] = useState(null);
    
    // REGLAS PARAMETRIZADAS (Valores por defecto)
    const [gameRules, setGameRules] = useState({
        duration: 60,
        ballsToSpawn: 5,
        cardInterval: 12,
        trophySeconds: 10,
        playerScale: 3,   // <-- NUEVO: Tamaño del jugador (3x por defecto)
        itemScale: 1.5    // <-- NUEVO: Tamaño de balones/tarjetas (1.5x por defecto)
    });

    // ==========================================================================
    // ESCUCHA DE WEBSOCKET (Inyectado desde Screen.js)
    // ==========================================================================
    useEffect(() => {
        if (!incomingData) return;

        const { action, payload } = incomingData;

        switch (action) {
            case 'show_frontpage':
                setView('frontpage');
                destroyGame();
                break;
            case 'show_ranking':
                setView('ranking');
                destroyGame();
                break;
            case 'update_ranking':
                if (payload?.rankingList) setRankingList(payload.rankingList);
                break;
            case 'player_ready':
                setCurrentPlayer(payload);
                setView('game');
                break;
            case 'gamepad_command':
                window.currentGamepadCommand = payload.command;
                break;
            case 'force_end':
                window.forceCatchGameEnd = true; 
                break;
            case 'set_rules':
                // Actualizamos las reglas si el Admin las cambia
                if (payload) setGameRules(prev => ({ ...prev, ...payload }));
                break;
            default:
                break;
        }
    }, [incomingData]);

    // ==========================================================================
    // MOTOR DE JUEGO PHASER
    // ==========================================================================
    useEffect(() => {
        if (view !== 'game' || !currentPlayer) return;
        if (gameInstanceRef.current) return;

        window.currentGamepadCommand = 'stop';
        window.forceCatchGameEnd = false;

        const config = {
            type: Phaser.AUTO,
            width: 1920, 
            height: 1080,
            parent: gameContainerRef.current,
            transparent: true, 
            scale: {
                mode: Phaser.Scale.FIT,
                autoCenter: Phaser.Scale.CENTER_BOTH
            },
            physics: {
                default: 'arcade',
                arcade: { gravity: { y: 400 }, debug: false }
            },
            scene: { preload, create, update }
        };

        const game = new Phaser.Game(config);
        gameInstanceRef.current = game;

        // Variables de lógica de juego usando las REGLAS PARAMETRIZADAS
        let player, stars, bombs, trophies, platformGroup;
        let score = 0, timeLeft = gameRules.duration, cardsReceived = 0, starsCollected = 0; 
        let scoreText, timerText, nicknameText, warnText;
        let isGameActive = true;

        function preload() {
            this.load.image('ground', '/assets/platform.png');
            this.load.image('star', '/assets/star.png'); // Balón
            this.load.image('trophy', '/assets/trophy.png'); // Escudo del equipo
            this.load.image('card_yellow', '/assets/tarjeta_amarilla.png');
            this.load.image('card_red', '/assets/tarjeta_roja.png');
            this.load.spritesheet('dude', '/assets/dude.png', { frameWidth: 32, frameHeight: 48 });
        }

        function create() {
            const width = this.scale.width;
            const height = this.scale.height;

            // 1. Suelo
            platformGroup = this.physics.add.staticGroup();
            const ground = platformGroup.create(width / 2, height - 20, 'ground');
            ground.setDisplaySize(width, 60).refreshBody();

            // 2. Jugador
            player = this.physics.add.sprite(width / 2, height - 150, 'dude');
            //player.setScale(3); 
            player.setScale(gameRules.playerScale);
            player.setBounce(0.1);
            player.setCollideWorldBounds(true);
            this.physics.add.collider(player, platformGroup);

            this.anims.create({ key: 'left', frames: this.anims.generateFrameNumbers('dude', { start: 0, end: 3 }), frameRate: 15, repeat: -1 });
            this.anims.create({ key: 'turn', frames: [{ key: 'dude', frame: 4 }], frameRate: 20 });
            this.anims.create({ key: 'right', frames: this.anims.generateFrameNumbers('dude', { start: 5, end: 8 }), frameRate: 15, repeat: -1 });

            // 3. Balones iniciales (Rebotan en el suelo)
            stars = this.physics.add.group({
                key: 'star',
                repeat: 9, // 10 balones iniciales
                setXY: { x: 50, y: 0, stepX: (width - 100) / 9 }
            });
            stars.children.iterate((child) => {
                //child.setScale(1.5);
                child.setScale(gameRules.itemScale);
                child.setBounceY(Phaser.Math.FloatBetween(0.4, 0.8));
                child.setCollideWorldBounds(true);
            });
            this.physics.add.collider(stars, platformGroup);

            // 4. Grupos de Tarjetas y Escudos
            bombs = this.physics.add.group();
            this.physics.add.collider(bombs, platformGroup);

            trophies = this.physics.add.group();
            this.physics.add.collider(trophies, platformGroup);

            // 5. Overlaps (Recolección)
            this.physics.add.overlap(player, stars, collectStar, null, this);
            this.physics.add.collider(player, bombs, hitCard, null, this);
            this.physics.add.overlap(player, trophies, collectTrophy, null, this);

            // 6. UI Texts
            const textStyle = { fontFamily: '"Russo One", sans-serif', fontSize: '64px', fill: '#FFF', stroke: '#000', strokeThickness: 8 };
            nicknameText = this.add.text(width / 2, 50, `JUGANDO: ${currentPlayer.nickname.toUpperCase()}`, { ...textStyle, fill: '#f1c40f' }).setOrigin(0.5);
            scoreText = this.add.text(50, 50, 'PUNTOS: 0', textStyle);
            
            const mins = Math.floor(timeLeft / 60).toString().padStart(2, '0');
            const secs = (timeLeft % 60).toString().padStart(2, '0');
            timerText = this.add.text(width - 50, 50, `${mins}:${secs}`, textStyle).setOrigin(1, 0);

            // 7. Temporizadores
            // Reloj de partida
            this.time.addEvent({
                delay: 1000,
                callback: () => {
                    if (!isGameActive) return;
                    timeLeft--;
                    const m = Math.floor(timeLeft / 60).toString().padStart(2, '0');
                    const s = (timeLeft % 60).toString().padStart(2, '0');
                    timerText.setText(`${m}:${s}`);
                    if (timeLeft <= 10) timerText.setTint(0xff5555); 
                    if (timeLeft <= 0) endGame(this);
                },
                callbackScope: this,
                loop: true
            });

            // Generador de Escudos Parametrizado
            this.time.addEvent({
                delay: gameRules.trophySeconds * 1000, 
                callback: spawnTrophy,
                callbackScope: this,
                loop: true
            });
        }

        function update() {
            if (!isGameActive) return;

            // BOTÓN DE PÁNICO (Admin)
            if (window.forceCatchGameEnd) {
                window.forceCatchGameEnd = false;
                endGame(this, false); 
                return;
            }

            // CONTROLES DE MOVIMIENTO
            const cmd = window.currentGamepadCommand;
            const speed = 700; 

            if (cmd === 'left') {
                player.setVelocityX(-speed);
                player.anims.play('left', true);
            } else if (cmd === 'right') {
                player.setVelocityX(speed);
                player.anims.play('right', true);
            } else {
                player.setVelocityX(0);
                player.anims.play('turn');
            }
        }

        function collectStar(player, star) {
            star.disableBody(true, true);
            score += 1;
            starsCollected++;
            scoreText.setText('PUNTOS: ' + score);

            if (stars.countActive(true) === 0) {
                stars.children.iterate((child) => {
                    child.enableBody(true, Phaser.Math.Between(50, 1870), 0, true, true);
                    child.setVelocity(0, 0);
                });
            }

            // Regla parametrizada: Caída de balones
            if (starsCollected % gameRules.ballsToSpawn === 0) {
                for (let i = 0; i < gameRules.ballsToSpawn; i++) {
                    const x = Phaser.Math.Between(50, 1870);
                    const newStar = stars.create(x, 0, 'star');
                    //newStar.setScale(1.5);
                    newStar.setScale(gameRules.itemScale);
                    newStar.setBounceY(Phaser.Math.FloatBetween(0.4, 0.8));
                    newStar.setCollideWorldBounds(true);
                    newStar.setVelocity(Phaser.Math.Between(-100, 100), 20);
                }
            }

            // Regla parametrizada: Caída de tarjetas
            if (starsCollected % gameRules.cardInterval === 0 && starsCollected > 0) {
                for (let i = 0; i < 2; i++) {
                    const isRed = cardsReceived > 0;
                    const card = bombs.create(Phaser.Math.Between(50, 1870), 16, isRed ? 'card_red' : 'card_yellow');
                    //card.setScale(1.5);
                    card.setScale(gameRules.itemScale);
                    card.setBounce(1);
                    card.setCollideWorldBounds(true);
                    card.setVelocity(Phaser.Math.Between(-300, 300), 100);
                }
            }
        }

        function spawnTrophy() {
            if (!isGameActive) return;
            const x = Phaser.Math.Between(100, 1820);
            const trophy = trophies.create(x, 0, 'trophy');
            //trophy.setScale(1.5);
            trophy.setScale(gameRules.itemScale);
            trophy.setBounce(1);
            trophy.setCollideWorldBounds(true);
            trophy.setVelocity(Phaser.Math.Between(-300, 300), 20);

            this.time.delayedCall(5000, () => { if (trophy.active) trophy.destroy(); });
        }

        function collectTrophy(player, trophy) {
            trophy.destroy();
            score += 10;
            scoreText.setText('PUNTOS: ' + score);

            let txt = this.add.text(player.x, player.y - 50, '+10', {
                fontFamily: '"Russo One", sans-serif', fontSize: '64px', fill: '#00FF00', stroke: '#000', strokeThickness: 6
            }).setOrigin(0.5);

            this.tweens.add({ targets: txt, y: player.y - 150, alpha: 0, duration: 1000, onComplete: () => txt.destroy() });
        }

        function hitCard(player, card) {
            card.disableBody(true, true);
            cardsReceived++;
            this.cameras.main.shake(200, 0.02);
            player.setTint(0xff0000);
            this.time.delayedCall(500, () => { if (isGameActive) player.clearTint(); });

            if (cardsReceived === 1) {
                warnText = this.add.text(player.x, player.y - 100, '¡AMONESTADO!', { fontFamily: '"Russo One", sans-serif', fontSize: '64px', fill: '#FFFF00', stroke: '#000', strokeThickness: 8 }).setOrigin(0.5);
                this.tweens.add({ targets: warnText, y: player.y - 250, alpha: 0, scale: 1.2, duration: 2000, onComplete: () => warnText.destroy() });
                bombs.children.iterate((c) => { if (c.active) c.setTexture('card_red'); });
            } else if (cardsReceived >= 2) {
                endGame(this, true);
            }
        }

        function endGame(scene, isExpelled = false) {
            isGameActive = false;
            player.setVelocityX(0);
            player.anims.play('turn');
            player.setTint(0x555555);

            const bgRect = scene.add.rectangle(1920/2, 1080/2, 1920, 1080, 0x000000, 0.8);
            const msg = isExpelled ? '¡EXPULSADO!' : '¡TIEMPO AGOTADO!';
            const color = isExpelled ? '#FF0000' : '#FFFFFF';
            
            scene.add.text(1920/2, 1080/2 - 50, msg, { fontFamily: '"Russo One", sans-serif', fontSize: '110px', fill: color, stroke: '#000', strokeThickness: 10 }).setOrigin(0.5);
            scene.add.text(1920/2, 1080/2 + 100, `Puntos Finales: ${score}`, { fontFamily: '"Russo One", sans-serif', fontSize: '80px', fill: '#f1c40f', stroke: '#000', strokeThickness: 8 }).setOrigin(0.5);

            handleGameFinished(score);
        }

        return () => destroyGame();
    }, [view, currentPlayer]);

    const destroyGame = () => {
        if (gameInstanceRef.current) {
            gameInstanceRef.current.destroy(true);
            gameInstanceRef.current = null;
        }
    };

    const handleGameFinished = async (finalScore) => {
        try {
            await fetch(`${process.env.REACT_APP_API_URL}CatchTheBall/SubmitScore`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...currentPlayer, score: finalScore })
            });
        } catch (error) {
            console.error("Error guardando score", error);
        }

        setTimeout(() => {
            setView('ranking');
            destroyGame();
            setCurrentPlayer(null);
        }, 5000);
    };

    return (
        <>
            <div className="catch-screen">
                <div className="catch-screen__bg-overlay"></div>

                {view === 'frontpage' && (
                    <div className="catch-screen__frontpage">
                        <img src="/img/page/adsl-five.png" alt="Logo" className="catch-screen__logo" style={{ width: '450px' }} />
                        <h1 className="catch-screen__title">¡ATRAPA LOS BALONES!</h1>
                        {/* <h3 className="catch-screen__subtitle">Prepara tu celular para jugar</h3> */}
                        <div className="catch-screen__bouncing-ball">⚽</div>
                    </div>
                )}

                {view === 'ranking' && (
                    <div className="catch-screen__ranking-view">
                        <h2 className="catch-screen__ranking-title"><i className="bi bi-trophy-fill text-warning"></i> TOP 10 MEJORES JUGADORES</h2>
                        <div className="catch-screen__ranking-list">
                            {rankingList.length === 0 ? (
                                <h3 className="text-muted mt-5">Esperando al primer jugador...</h3>
                            ) : (
                                rankingList.map((player, index) => (
                                    <div key={index} className="catch-screen__ranking-item">
                                        <div className={`catch-screen__ranking-pos pos-${index + 1}`}>#{index + 1}</div>
                                        <div className="catch-screen__ranking-name">{player.nickname.toUpperCase()}</div>
                                        <div className="catch-screen__ranking-score">{player.score} PTS</div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                <div 
                    ref={gameContainerRef} 
                    style={{ width: '100%', height: '100%', display: view === 'game' ? 'block' : 'none' }}
                />
            </div>

            <p className='FooterCredits' style={{ position: 'fixed', bottom: '0', left: '50%', transform: 'translateX(-50%)', zIndex: '201' }}>Hecho con ♥ por Interaktia | 2026</p>
        </>
    );
};

export { CatchTheBall };